import React, { useContext, useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Popover,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  CircularProgress,
} from '@mui/material'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import MyLocationIcon from '@mui/icons-material/MyLocation'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded'
import WorkOutlineRoundedIcon from '@mui/icons-material/WorkOutlineRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import UserContext from '../../context/User'
import { addressToLocation, pointOf } from '../PickupNearby/pickupText'
import { useLocationContext } from '../../context/Location'
import { useLocation } from '../../hooks'
import useMapboxAutocomplete from '../../hooks/useMapboxAutocomplete'
import { cityLine, shortAddress } from '../../utils/shortAddress'
import { C, pillTriggerSx } from '../../utils/designTokens'

// `renderTrigger` swaps the trigger for the caller's own button (the
// no-delivery view's "Change or add address"); it gets the props to spread.
const savedIcon = (label) => {
  const l = String(label || '').toLowerCase()
  if (l === 'apartment') return ApartmentRoundedIcon
  if (l === 'work' || l === 'office') return WorkOutlineRoundedIcon
  if (l === 'house' || l === 'home') return HomeOutlinedIcon
  return LocationOnOutlinedIcon
}

const AddressSelector = ({ variant, maxLabelWidth = 160, renderTrigger }) => {
  const { location, setLocation } = useLocationContext()
  const { profile, isLoggedIn } = useContext(UserContext)
  // The account's saved addresses, once each (the same label and address
  // saved twice shows one row), and only ones with a point to search from.
  const saved = (isLoggedIn ? profile?.addresses || [] : [])
    .filter((a) => a?.deliveryAddress && pointOf(a))
    .filter((a, i, all) => all.findIndex((b) => b.deliveryAddress === a.deliveryAddress && (b.label || '') === (a.label || '')) === i)
  const isCurrent = (a) =>
    Boolean(location) && (location._id ? String(location._id) === String(a._id) : location.deliveryAddress === a.deliveryAddress)
  const currentIsSaved = saved.some(isCurrent)
  const { getCurrentLocation } = useLocation()
  const { options, setOptions, fetchPredictions } = useMapboxAutocomplete()
  const [anchorEl, setAnchorEl] = useState(null)
  const [inputValue, setInputValue] = useState('')
  const [gettingLocation, setGettingLocation] = useState(false)

  const open = Boolean(anchorEl)

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setInputValue('')
    setOptions([])
  }

  useEffect(() => {
    fetchPredictions(inputValue)
  }, [inputValue, fetchPredictions])

  const handleSelectAddress = (option) => {
    if (option && option.lat != null) {
      setLocation({
        label: option.description,
        deliveryAddress: option.description,
        latitude: option.lat,
        longitude: option.lng,
      })
      handleClose()
    }
  }

  const handleSelectSaved = (a) => {
    setLocation(addressToLocation(a))
    handleClose()
  }

  const handleGetCurrentLocation = () => {
    setGettingLocation(true)
    getCurrentLocation((error, data) => {
      setGettingLocation(false)
      if (!error && data) {
        setLocation(data)
        handleClose()
      }
    })
  }

  // Show short version of address for header display: "Salem, MA".
  const getDisplayAddress = () => {
    if (!location) return 'Select address'
    return shortAddress(location.deliveryAddress) || location.label || 'Select address'
  }

  return (
    <>
      {renderTrigger ? (
        renderTrigger({ onClick: handleClick, 'aria-haspopup': 'dialog', 'aria-expanded': open })
      ) : /* "pill": the place half of the top bar's "Salem, MA · Now" pill. */
      variant === 'pill' ? (
        <Box
          component="button"
          type="button"
          onClick={handleClick}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={`Delivery address: ${getDisplayAddress()}. Change address`}
          title={location?.deliveryAddress || undefined}
          sx={{ ...pillTriggerSx, minWidth: 0, backgroundColor: open ? C.surface2 : 'transparent' }}
        >
          <LocationOnOutlinedIcon sx={{ color: C.ink, fontSize: 18, flexShrink: 0 }} />
          <Box
            component="span"
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: maxLabelWidth }}
          >
            {(location && cityLine(location.deliveryAddress)) || getDisplayAddress()}
          </Box>
        </Box>
      ) : (
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '8px 12px',
          cursor: 'pointer',
          borderRadius: '8px',
          flexShrink: 1,
          minWidth: 0,
          backgroundColor: open ? 'rgba(0,0,0,0.04)' : 'transparent',
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.04)',
          },
        }}
      >
        <LocationOnOutlinedIcon sx={{ color: '#191919', fontSize: 20, flexShrink: 0 }} />
        <Typography
          sx={{
            color: '#191919',
            fontSize: '0.875rem',
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '150px',
          }}
        >
          {getDisplayAddress()}
        </Typography>
        <ExpandMoreIcon
          sx={{
            color: '#767676',
            fontSize: 18,
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}
        />
      </Box>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            width: 380,
            maxHeight: 'min(560px, calc(100vh - 96px))',
            borderRadius: '12px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
            mt: 1,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '1rem' }}>
              Delivery address
            </Typography>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <TextField
            fullWidth
            placeholder="Search for an address"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#767676' }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: '8px',
                backgroundColor: '#F5F5F5',
                '& fieldset': { border: 'none' },
              },
            }}
            sx={{ mb: 1 }}
          />

          {/* Current Location Button */}
          <ListItem
            button
            onClick={handleGetCurrentLocation}
            sx={{
              borderRadius: '8px',
              mb: 1,
              '&:hover': { backgroundColor: '#F5F5F5' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              {gettingLocation ? (
                <CircularProgress size={20} sx={{ color: '#CC0000' }} />
              ) : (
                <MyLocationIcon sx={{ color: '#CC0000' }} />
              )}
            </ListItemIcon>
            <ListItemText
              primary="Use current location"
              primaryTypographyProps={{
                sx: { fontWeight: 500, color: '#CC0000' },
              }}
            />
          </ListItem>

          <Divider sx={{ my: 1 }} />

          {/* Search Results */}
          {options.length > 0 && (
            <List sx={{ maxHeight: 250, overflow: 'auto' }}>
              {options.map((option, index) => (
                <ListItem
                  key={option.place_id || index}
                  button
                  onClick={() => handleSelectAddress(option)}
                  sx={{
                    borderRadius: '8px',
                    '&:hover': { backgroundColor: '#F5F5F5' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <LocationOnIcon sx={{ color: '#767676' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={option.structured_formatting?.main_text}
                    secondary={option.structured_formatting?.secondary_text}
                    primaryTypographyProps={{ sx: { fontWeight: 500 } }}
                    secondaryTypographyProps={{ sx: { fontSize: '0.75rem' } }}
                  />
                </ListItem>
              ))}
            </List>
          )}

          {/* Current Address Display — unless it's one of the saved ones below, which mark it. */}
          {options.length === 0 && location && !currentIsSaved && (
            <Box sx={{ mt: 1 }}>
              <Typography sx={{ fontSize: '0.75rem', color: '#767676', mb: 1 }}>
                Current address
              </Typography>
              <Box
                sx={{
                  p: 1.5,
                  backgroundColor: '#F5F5F5',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <LocationOnIcon sx={{ color: '#CC0000' }} />
                <Typography sx={{ fontSize: '0.875rem' }}>
                  {location.deliveryAddress || location.label}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Saved addresses: one tap to switch. */}
          {options.length === 0 && saved.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography id="saved-addresses-title" sx={{ fontSize: '0.75rem', color: '#767676', mb: 0.5 }}>
                Saved addresses
              </Typography>
              <List aria-labelledby="saved-addresses-title" disablePadding sx={{ maxHeight: 220, overflow: 'auto' }}>
                {saved.map((a) => {
                  const Icon = savedIcon(a.label)
                  const current = isCurrent(a)
                  return (
                    <ListItem
                      key={a._id}
                      button
                      onClick={() => handleSelectSaved(a)}
                      aria-current={current ? 'true' : undefined}
                      sx={{
                        borderRadius: '8px',
                        mb: 0.5,
                        backgroundColor: current ? '#F5F5F5' : 'transparent',
                        '&:hover': { backgroundColor: '#F5F5F5' },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Icon sx={{ color: current ? '#CC0000' : '#767676' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={a.label || 'Saved address'}
                        secondary={a.deliveryAddress}
                        primaryTypographyProps={{ sx: { fontWeight: 600, fontSize: '0.9rem' } }}
                        secondaryTypographyProps={{ sx: { fontSize: '0.78rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }}
                        sx={{ minWidth: 0 }}
                      />
                      {current && <CheckRoundedIcon aria-label="Current address" sx={{ color: '#CC0000', fontSize: 20, ml: 1 }} />}
                    </ListItem>
                  )
                })}
              </List>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  )
}

export default AddressSelector
